import { useState, useCallback, useRef, useEffect } from 'react';
import { calculatePopupPosition, getAssignmentConflicts } from '../../../../utils/leaderUtils';

/**
 * Custom hook to manage UI interactions: Hover effects, Context Menus, and Clipboard.
 * Also handles the bridge between D&D and popups.
 * 
 * @param {Object} options
 * @returns {Object} { states, handlers, refs }
 */
export function useLeaderInteractions({
    assignmentsForConflicts,
    absencesForConflicts,
    vehicles,
    isConflictDetectionEnabled,
    onAssignmentClick,
    onAssignmentDelete,
    onAssignmentSave,
    onMoveToUndated,
    toLocalISO,
    interaction,
    setInteraction
}) {
    // --- State ---
    const [externalDraggingAssignment, setExternalDraggingAssignment] = useState(null);
    const [hoveredAssignment, setHoveredAssignment] = useState(null);
    const [hoverCardPos, setHoverCardPos] = useState(null);
    const [hoveredAssignmentConflicts, setHoveredAssignmentConflicts] = useState(null);
    const [hoveredConflict, setHoveredConflict] = useState(null);
    const [hoveredConflictPos, setHoveredConflictPos] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [clipboard, setClipboard] = useState(null);

    // --- Refs ---
    const hoverDelayRef = useRef(null);
    const hoverCloseDelayRef = useRef(null);
    const conflictHoverDelayRef = useRef(null);
    const conflictHoverCloseDelayRef = useRef(null);

    // --- Cleanup ---
    useEffect(() => {
        return () => {
            if (hoverDelayRef.current) clearTimeout(hoverDelayRef.current);
            if (conflictHoverDelayRef.current) clearTimeout(conflictHoverDelayRef.current);
            if (conflictHoverCloseDelayRef.current) clearTimeout(conflictHoverCloseDelayRef.current);
            if (hoverCloseDelayRef.current) clearTimeout(hoverCloseDelayRef.current);
        };
    }, []);

    // --- Helpers ---
    const getPopupPosition = useCallback((triggerRect, cardWidth, estimatedHeight, fallbackPoint = null, options = {}) => {
        return calculatePopupPosition(triggerRect, cardWidth, estimatedHeight, fallbackPoint, options);
    }, []);

    const getConflictPopupPosition = useCallback((rect, cw, ch, fp, opts) => 
        getPopupPosition(rect, cw, ch, fp, { ...opts, isConflict: true }), [getPopupPosition]);

    const onHoverCardEnter = useCallback(() => {
        if (hoverCloseDelayRef.current) {
            clearTimeout(hoverCloseDelayRef.current);
            hoverCloseDelayRef.current = null;
        }
    }, []);

    const onConflictHoverCardEnter = useCallback(() => {
        if (conflictHoverCloseDelayRef.current) {
            clearTimeout(conflictHoverCloseDelayRef.current);
            conflictHoverCloseDelayRef.current = null;
        }
    }, []);

    const clearAssignmentHover = useCallback(() => {
        if (hoverDelayRef.current) {
            clearTimeout(hoverDelayRef.current);
            hoverDelayRef.current = null;
        }
        if (hoverCloseDelayRef.current) clearTimeout(hoverCloseDelayRef.current);
        
        hoverCloseDelayRef.current = setTimeout(() => {
            setHoveredAssignment(null);
            setHoverCardPos(null);
            setHoveredAssignmentConflicts(null);
            hoverCloseDelayRef.current = null;
        }, 400); // Premium Bridge Delay
    }, []);

    const handleAssignmentHover = useCallback((assignment, event, options = {}) => {
        if (interaction || contextMenu || !assignment || !event?.currentTarget) return;
        
        if (hoverDelayRef.current) clearTimeout(hoverDelayRef.current);
        if (hoverCloseDelayRef.current) clearTimeout(hoverCloseDelayRef.current);

        const triggerRect = event.currentTarget.getBoundingClientRect();
        const fallbackPoint = { clientX: event.clientX, clientY: event.clientY };
        
        const isRightSidebar = options.isRightSidebar || !!event.currentTarget.closest('.undatierte-sidebar');
        const isListView = !!event.currentTarget.closest('.leader-list-view');

        hoverDelayRef.current = setTimeout(() => {
            const cardWidth = 320;
            const cardHeight = 320;

            const pos = getPopupPosition(
                triggerRect, 
                cardWidth, 
                cardHeight, 
                fallbackPoint, 
                { 
                    isSidebar: isRightSidebar, 
                    preferLeft: isRightSidebar,
                    useTriggerOnly: !isListView 
                }
            );
            
            // FIX: Calculate conflicts for the hover card
            const conflicts = isConflictDetectionEnabled 
                ? getAssignmentConflicts(assignment, assignmentsForConflicts, absencesForConflicts, vehicles)
                : null;

            setHoveredAssignment(assignment);
            setHoveredAssignmentConflicts(conflicts);
            setHoverCardPos(pos);
        }, 250); // Premium Hover Delay
    }, [interaction, contextMenu, getPopupPosition, isConflictDetectionEnabled, assignmentsForConflicts, absencesForConflicts, vehicles]);

    const handleExportAssignmentHover = useCallback((assignment, event) => {
        if (!assignment || !event?.currentTarget) return;
        
        if (hoverDelayRef.current) clearTimeout(hoverDelayRef.current);
        if (hoverCloseDelayRef.current) clearTimeout(hoverCloseDelayRef.current);

        const triggerRect = event.currentTarget.getBoundingClientRect();
        const fallbackPoint = { clientX: event.clientX, clientY: event.clientY };
        const isListView = !!event.currentTarget.closest('.leader-list-view');

        hoverDelayRef.current = setTimeout(() => {
            const cardWidth = 320;
            const cardHeight = 280;
            
            const pos = getPopupPosition(
                triggerRect, 
                cardWidth, 
                cardHeight, 
                fallbackPoint, 
                { 
                    useTriggerOnly: !isListView
                }
            );
            
            setHoveredAssignment(assignment);
            setHoveredAssignmentConflicts(null); // Export hover card usually doesn't show full conflict list to stay compact
            setHoverCardPos({ ...pos, hideConflicts: true });
        }, 250);
    }, [getPopupPosition]);

    const clearConflictHover = useCallback(() => {
        if (conflictHoverDelayRef.current) {
            clearTimeout(conflictHoverDelayRef.current);
            conflictHoverDelayRef.current = null;
        }
        if (conflictHoverCloseDelayRef.current) clearTimeout(conflictHoverCloseDelayRef.current);
        
        conflictHoverCloseDelayRef.current = setTimeout(() => {
            setHoveredConflict(null);
            setHoveredConflictPos(null);
            conflictHoverCloseDelayRef.current = null;
        }, 350);
    }, []);

    const handleConflictHover = useCallback((conflict, assignment, event) => {
        if (interaction || contextMenu || !conflict?.blocks?.length || !event?.currentTarget) return;
        
        if (conflictHoverDelayRef.current) clearTimeout(conflictHoverDelayRef.current);
        if (conflictHoverCloseDelayRef.current) clearTimeout(conflictHoverCloseDelayRef.current);

        const triggerRect = event.currentTarget.getBoundingClientRect();
        const fallbackPoint = { clientX: event.clientX, clientY: event.clientY };
        const isRightSidebarContext = !!event.currentTarget.closest('.undatierte-sidebar');
        const isListViewContext = !!event.currentTarget.closest('.leader-list-view');
        
        const isSidebar = !assignment;
        if (isSidebar) {
            clearAssignmentHover();
        }

        conflictHoverDelayRef.current = setTimeout(() => {
            const conflictPos = getConflictPopupPosition(
                triggerRect, 
                300, 
                Math.min(850, Math.round(window.innerHeight * 0.85)), 
                fallbackPoint, 
                { 
                    isSidebar: isSidebar || isRightSidebarContext,
                    preferLeft: isRightSidebarContext,
                    useTriggerOnly: !isListViewContext
                }
            );

            setHoveredConflict({ blocks: conflict.blocks, title: conflict.title || '' });
            setHoveredConflictPos(conflictPos);
            
            if (assignment) {
                setHoveredAssignment(assignment);
                setHoverCardPos(getPopupPosition(triggerRect, 300, 320, fallbackPoint, { useTriggerOnly: !isListViewContext }));
            }
        }, 250);
    }, [interaction, contextMenu, clearAssignmentHover, getConflictPopupPosition, getPopupPosition]);

    // --- Context Menu Logic ---
    const handleContextMenu = useCallback((e, assignment, day = null, time = null) => {
        e.preventDefault();
        e.stopPropagation();

        // Close all popups immediately on right-click
        if (hoverDelayRef.current) clearTimeout(hoverDelayRef.current);
        if (hoverCloseDelayRef.current) clearTimeout(hoverCloseDelayRef.current);
        if (conflictHoverDelayRef.current) clearTimeout(conflictHoverDelayRef.current);
        if (conflictHoverCloseDelayRef.current) clearTimeout(conflictHoverCloseDelayRef.current);
        
        setHoveredAssignment(null);
        setHoverCardPos(null);
        setHoveredAssignmentConflicts(null);
        setHoveredConflict(null);
        setHoveredConflictPos(null);

        let x = e.clientX;
        let y = e.clientY;
        const menuWidth = 160;
        const menuHeight = 150;
        const submenuWidth = 140;
        
        if (x + menuWidth > window.innerWidth) x -= menuWidth;
        if (y + menuHeight > window.innerHeight) y -= menuHeight;
        
        const openLeft = (x + menuWidth + submenuWidth > window.innerWidth);
        setContextMenu({ x, y, assignment, day, time, openLeft });
    }, []);

    const handleMenuAction = useCallback(async (action, data) => {
        setContextMenu(null);
        if (action === 'edit') {
            onAssignmentClick(data.assignment);
        } else if (action === 'delete') {
            if (window.confirm('Einsatz wirklich löschen?')) {
                onAssignmentDelete(data.assignment.id);
            }
        } else if (action === 'copy') {
            setClipboard(data.assignment);
        } else if (action === 'paste') {
            if (!clipboard) return;
            
            const { 
                id, created_at, updated_at, 
                user_id, company_id, 
                deleted_at,
                start_datetime: _s, end_datetime: _e, all_day: _a, status: _st,
                ...baseData 
            } = clipboard;
            
            const assignmentToSave = {
                ...baseData,
                status: clipboard.status || 'aktiv' // Fallback to 'aktiv' per system hygiene
            };
            
            if (data.day) {
                const newStart = new Date(data.day);
                if (data.time) {
                    newStart.setHours(data.time.getHours(), data.time.getMinutes());
                } else {
                    newStart.setHours(0, 0, 0, 0);
                }

                let durationMs = 60 * 60 * 1000; 
                if (!data.time && clipboard.start_datetime && clipboard.end_datetime) {
                    durationMs = new Date(clipboard.end_datetime) - new Date(clipboard.start_datetime);
                }

                const newEnd = new Date(newStart.getTime() + durationMs);

                onAssignmentSave({
                    ...assignmentToSave,
                    start_datetime: toLocalISO(newStart),
                    end_datetime: toLocalISO(newEnd),
                    all_day: !data.time
                });
            } else {
                onAssignmentSave({
                    ...assignmentToSave,
                    start_datetime: null,
                    end_datetime: null,
                    status: 'undatiert'
                });
            }
        } else if (action === 'status') {
            const { assignment, status } = data;
            onAssignmentSave({
                ...assignment,
                status: status,
                start_datetime: assignment.start_datetime ? toLocalISO(new Date(assignment.start_datetime)) : null,
                end_datetime: assignment.end_datetime ? toLocalISO(new Date(assignment.end_datetime)) : null
            });
        } else if (action === 'undated') {
            onMoveToUndated(data.assignment);
        }
    }, [onAssignmentClick, onAssignmentDelete, clipboard, onAssignmentSave, toLocalISO, onMoveToUndated]);

    return {
        // States
        externalDraggingAssignment, setExternalDraggingAssignment,
        hoveredAssignment,
        hoverCardPos,
        hoveredAssignmentConflicts,
        hoveredConflict,
        hoveredConflictPos,
        contextMenu, setContextMenu,
        clipboard, setClipboard,
        
        // Handlers
        handleAssignmentHover,
        handleExportAssignmentHover,
        clearAssignmentHover,
        handleConflictHover,
        clearConflictHover,
        handleContextMenu,
        handleMenuAction,
        onHoverCardEnter,
        onConflictHoverCardEnter,
        getPopupPosition
    };
}
